import { Box, Typography, FormText, FormTextArea, FormSelect, FeedbackMessage, Grid, TagButton, IconTextButton } from "@husqvarna/ui-core";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";

import { useCreateProjectMutation, useUpdateProjectMutation } from "../projectApi";
import { parseGeoFile, geoFeaturesToAreas, GeoFileParseError, isAcceptedFile } from "../utils/geoFileParser";
import type { Area } from "../models/Area";
import ProjectOverview from '../models/ProjectOverview';
import { useGetRegionsQuery, useGetUserRegionQuery } from "../../shared/api/settingsApi";
import UpdateProjectModel from "../models/UpdateProjectModel";
import { ProjectStatus, ProjectType } from "../models/Enums";
import { pushToDataLayer } from "../../shared/utils/tracking";
import { FormSelectOption } from "@husqvarna/ui-core/dist/components/form-select";
import UploadedFile from "./UploadedFile/UploadedFile";

export type ProjectFormRef = {
  submit: () => void;
};

type ProjectFormProps = {
  project?: ProjectOverview;
  onFormSubmitted?: (project: ProjectOverview | undefined) => void;
  onCancel?: () => void;
}

const createDefaultValues = (project?: ProjectOverview) => {
  if (!project) {
    return {};
  }

  const projectFormModel: UpdateProjectModel = {
    id: project.id,
    name: project.name,
    status: project.status,
    customerCompanyName: project.customerCompanyName,
    customerContactName: project.customerContactName,
    customerPhoneNumber: project.customerPhoneNumber,
    customerEmail: project.customerEmail,
    customerAddress: project.customerAddress,
    customerZipCode: project.customerZipCode,
    customerCity: project.customerCity,
    regionId: project.region?.id,
    projectGoal: project.projectGoal,
    type : project.type,
  }

  return projectFormModel;
}

const ProjectForm = forwardRef<ProjectFormRef, ProjectFormProps>(function ProjectForm(
  {
    project,
    onFormSubmitted,
    onCancel
  },
  ref
) {
  const intl = useIntl();
  const { register, handleSubmit, setValue, trigger, formState: { errors } } = useForm<UpdateProjectModel>({
    defaultValues: createDefaultValues(project)
  });

  const [createProject] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const { data: userRegion } = useGetUserRegionQuery();
  const { data: regions, isLoading } = useGetRegionsQuery();

  const [projectStatus, setProjectStatus] = useState(project?.status ?? ProjectStatus.InProgress);
  const [regionId, setRegionId] = useState(project?.region?.id);
  const [selectedProjectType, setSelectedProjectType] = useState<ProjectType | undefined>(project?.type);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [changedRegion, setChangedRegion] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parsedAreas, setParsedAreas] = useState<Area[] | undefined>(undefined);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const isNewProject = project === undefined;
  const emptySelectValue = " ";

  useEffect(() => {
    setValue("status", projectStatus);
  }, [projectStatus]);


  useEffect(() => {
    setValue("regionId", regionId ?? emptySelectValue, { shouldValidate: true });
    setChangedRegion(!isNewProject && (project?.region?.id !== regionId));
    trigger("regionId");
  }, [regionId, isNewProject, project?.region?.id]);

  useEffect(() => {
    setValue("type", selectedProjectType as ProjectType, { shouldValidate: true });
    if (selectedProjectType) {
      trigger("type");
    }
  }, [selectedProjectType, setValue, trigger]);

  useEffect(() => {
    if (isNewProject && userRegion != null && userRegion !== undefined) {
      const defaultRegion = regions?.find(x => x.id === userRegion.id);
      if (defaultRegion) {
        setRegionId(defaultRegion.id);
      }
    }
  }, [regions, userRegion]);

  // Register type field for validation
  useEffect(() => {
    register("type", {
      required: intl.formatMessage({
        id: "forms.editProjectInfo.fieldRequired",
        defaultMessage: "This field is required",
        description: "Form error message for required field",
      }),
    });
  }, [register, intl]);

  useEffect(() => {
    register("regionId", {
      required: intl.formatMessage({
        id: "forms.editProjectInfo.fieldRequired",
        defaultMessage: "This field is required",
        description: "Form error message for required field",
      }),
      validate: (value) =>
        (value && value !== emptySelectValue) ? true : intl.formatMessage({
          id: "forms.editProjectInfo.fieldRequired",
          defaultMessage: "This field is required",
          description: "Form error message for required field",
        }),
    });
  }, [register, intl]);

  const handleFileSelect = async (file: File) => {
    setUploadedFile(file);
    setParseError(null);
    setParsedAreas(undefined);
    setIsParsingFile(true);
    try {
      const features = await parseGeoFile(file);
      const areas = geoFeaturesToAreas(features);
      setParsedAreas(areas);
      if (areas.length === 0) {
        setParseError(
          intl.formatMessage({
            id: "project.editForm.uploadNoAreasFound",
            defaultMessage: "No polygon areas found in the file.",
          })
        );
      }
    } catch (err) {
      const message =
        err instanceof GeoFileParseError
          ? err.message
          : intl.formatMessage({
              id: "project.editForm.uploadParseFailed",
              defaultMessage: "Could not read areas from file. Please check the file format.",
            });
      setParseError(message);
      setParsedAreas(undefined);
    } finally {
      setIsParsingFile(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setParsedAreas(undefined);
    setParseError(null);
  };

  const onSubmit = async (model: UpdateProjectModel) => {
    setSubmitFailed(false);
    setParseError(null);

    if (!isNewProject) {
      const res = await updateProject(model);
      if ("error" in res) {
        setSubmitFailed(true);
        return;
      }
      const updatedProject = "data" in res ? res.data : undefined;
      trackProject(model);
      onFormSubmitted?.(updatedProject);
      return;
    }

    model.areas = parsedAreas;
    const res = await createProject(model);
    if ("error" in res) {
      setSubmitFailed(true);
      return;
    }

    const createdProject = "data" in res ? res.data : undefined;
    if (!createdProject) {
      onFormSubmitted?.(undefined);
      return;
    }

    trackProject(model, createdProject);
    onFormSubmitted?.(createdProject);
  };

  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit(onSubmit)();
    },
  }), [handleSubmit, onSubmit]);

  const projectTypeOptions : FormSelectOption[] = [
    {label: intl.formatMessage({id: "project.editForm.selectProjectType", defaultMessage: "Select project type"}), value: " ", disabled: true},
    {label: intl.formatMessage({id: "projectTypeOption.Other", defaultMessage: "Other"}), value: ProjectType.Other},
    {label: intl.formatMessage({id: "projectTypeOption.FacilityManagement", defaultMessage: "Facility management"}), value: ProjectType.FacilityManagement},
    {label: intl.formatMessage({id: "projectTypeOption.Golf", defaultMessage: "Golf"}), value: ProjectType.Golf},
    {label: intl.formatMessage({id: "projectTypeOption.HospitalityResort", defaultMessage: "Hospitality / resorts"}), value: ProjectType.HospitalityResort},
    {label: intl.formatMessage({id: "projectTypeOption.LandscapeContractor", defaultMessage: "Landscape contractors"}), value: ProjectType.LandscapeContractor},
    {label: intl.formatMessage({id: "projectTypeOption.MunicipalityPublicSite", defaultMessage: "Municipality & Public sites"}), value: ProjectType.MunicipalityPublicSite},
    {label: intl.formatMessage({id: "projectTypeOption.SchoolUniversity", defaultMessage: "School & universities"}), value: ProjectType.SchoolUniversity},
    {label: intl.formatMessage({id: "projectTypeOption.Sport", defaultMessage: "Sports (football, rugby, cricket etc)"}), value: ProjectType.Sport},
  ];

  const trackProject = (model: UpdateProjectModel, createdProject?: ProjectOverview) => {
    const eventName = isNewProject ? "createProject" : "updateProject";
    const selectedRegion = regions?.find(x => x.id === regionId);
    const projectId = isNewProject && createdProject ? `${createdProject.id}` : (isNewProject ? "undefined" : `${model.id}`);
    const projectInfo: Record<string, string | number | boolean | undefined> = {
      projectId,
      projectName: `${model.name}`,
      companyName: `${model.customerCompanyName}`,
      country: `${selectedRegion?.name}`,
    };
    if (isNewProject) {
      const hasImportedAreas = (model.areas?.length ?? 0) > 0;
      projectInfo.fileUpload = hasImportedAreas;
      projectInfo.uploadedAreasCount = model.areas?.length ?? 0;
      if (selectedProjectType) {
        projectInfo.projectType = selectedProjectType;
      }
    }
    pushToDataLayer({
      event: eventName,
      projectInfo,
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box container mb={10} gap={2}>
        <TagButton 
          text={intl.formatMessage({ id: "project.status.0", defaultMessage: "In progress" })} 
          active={projectStatus === ProjectStatus.InProgress}
          onClick={() => setProjectStatus(ProjectStatus.InProgress)} />
        <TagButton 
          text={intl.formatMessage({ id: "project.status.1", defaultMessage: "Completed" })} 
          active={projectStatus === ProjectStatus.Completed}
          onClick={() => setProjectStatus(ProjectStatus.Completed)}/>
      </Box>
      <FormText
        label={intl.formatMessage({ id: "project.editForm.projectName", defaultMessage: "Project name" })}
        {...register("name", {
          required: intl.formatMessage({
            id: "forms.editProjectInfo.fieldRequired",
            defaultMessage: "This field is required",
            description: "Form error message for required field",
          }),
        })}
        error={errors.name?.message}></FormText>
      <Box>
        <FormSelect mt={5} width="full"
                  label={intl.formatMessage({ id: "project.editForm.projectType", defaultMessage: "Project type" })}
                  options={projectTypeOptions}
                  selectedValue={selectedProjectType ?? " "}
                  error={errors.type?.message}
                  onChangeListener={(e: any) => {
                    const value = e.target?.value || e.value || e;
                    // Only set if it's a valid ProjectType (not the placeholder)
                    if (value && value !== "" && Object.values(ProjectType).includes(value)) {
                      setSelectedProjectType(value);
                    } else {
                      setSelectedProjectType(undefined);
                    }
                  }}/>
      </Box>
      {isNewProject && (
        <Box mt={7}>
          <Typography tag="h3" variant="Body2" className="tcsp-medium" text={intl.formatMessage({ id: "project.editForm.uploadAreas", defaultMessage: "Upload map from file" })} />
          <Typography tag="label" variant="Microcopy" htmlFor="project-areas-file">
            <FormattedMessage
              id="project.editForm.uploadAreasLabel"
              defaultMessage="Supported formats are KML and GeoJSON files with a maximum size of 10 MB"
            />
          </Typography>
            <Box mt={2}>
              <IconTextButton className="secondary" iconId="download" mt={2} variant="Body2" text={intl.formatMessage({ id: "project.editForm.uploadAreasButtonText", defaultMessage: "Click to upload" })} onClick={() => {
                document.getElementById("project-areas-file")?.click();
              }} />
              <input
                id="project-areas-file"
                hidden
                type="file"
                accept=".kml,.kmz,.geojson,.json"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && isAcceptedFile(file)) {
                    handleFileSelect(file);
                  } else if (file) {
                    setParseError(
                      intl.formatMessage({
                        id: "project.editForm.uploadInvalidType",
                        defaultMessage: "Please choose a .kml, .kmz or .geojson file.",
                      })
                    );
                  }
                  e.target.value = "";
                }}
                style={{ fontSize: "14px" }}
              />
              { uploadedFile && (
                <UploadedFile
                  name={uploadedFile.name}
                  error={parseError}
                  isLoading={isParsingFile}
                  onRemove={handleRemoveFile}
                />
              )}
            </Box>
        </Box>
      )}
      <Typography tag="h3" variant="Body1" className="tcsp-medium" mt={12} mb={4}>
        <FormattedMessage id="project.editForm.customerInformation" defaultMessage="Customer information" />
      </Typography>
      <FormText label={intl.formatMessage({ id: "project.editForm.companyName", defaultMessage: "Company name" })}
        {...register("customerCompanyName")} />
      <FormText label={intl.formatMessage({ id: "project.editForm.contactName", defaultMessage: "Contact name" })} mt={5}
        {...register("customerContactName")} />
      <Box mt={5}>
        <Grid container spacing={4}>
          <Grid item sm={7}>
              <FormText label={intl.formatMessage({ id: "project.editForm.email", defaultMessage: "Email" })}
                {...register("customerEmail")} />
          </Grid>
          <Grid item sm={5}>
            <FormText label={intl.formatMessage({ id: "project.editForm.mobileNumber", defaultMessage: "Mobile number" })}
              {...register("customerPhoneNumber")} />
          </Grid>
        </Grid>
      </Box>
      
      
      <Box mt={5}>
        <Grid container spacing={4}>
          <Grid item sm={7}>
            <FormText label={intl.formatMessage({ id: "project.editForm.address", defaultMessage: "Address" })} {...register("customerAddress")} />
          </Grid>
          <Grid item sm={5}>
            <FormText label={intl.formatMessage({ id: "project.editForm.zipCode", defaultMessage: "Zipcode" })} {...register("customerZipCode")} />
          </Grid>
        </Grid>
      </Box>
      <Box mt={5}>
        <Grid container spacing={4}>
          <Grid item sm={7}>
            <FormText label={intl.formatMessage({ id: "project.editForm.city", defaultMessage: "City" })} {...register("customerCity")} />
          </Grid>
          <Grid item sm={5}>
          {!isLoading && regions && (
              <FormSelect
                width="full"
                label={intl.formatMessage({ id: "project.editForm.country", defaultMessage: "Country" })}
                options={[
                  {
                    label: intl.formatMessage({ id: "project.editForm.selectCountry", defaultMessage: "Select country" }),
                    value: emptySelectValue,
                    disabled: true,
                  },
                  ...regions.map(region => ({ label: region.name, value: region.id })),
                ]}
                selectedValue={regionId ?? emptySelectValue}
                error={errors.regionId?.message}
                onChangeListener={(e: any) => {
                  const value = e.target?.value ?? e.value ?? e;
                  setRegionId(value === emptySelectValue ? undefined : value);
                }}
              />
            )}
          </Grid>
        </Grid>
            {changedRegion && !isNewProject && (
              <Typography tag="p" variant="Microcopy" mt={4} color="ErrorRed">
                <FormattedMessage
                  id="project.editForm.changedRegionWarning"
                  defaultMessage="If you change country, equipment not available on the new market will be removed. Make sure to review cost settings affected by currency change."
                  description="Description that some equipment are tied to a country, and when svaing the change TCSP removes the not applicable equipments."
                />
              </Typography>
            )}
      </Box>
      <Typography tag="h3" variant="Body1" className="tcsp-medium" mt={7} mb={4}>
        <FormattedMessage id="project.editForm.setAGoal" defaultMessage="Target goal" />
      </Typography>
      <FormTextArea mb={6} label={intl.formatMessage({
        id: "project.editForm.goal",
        defaultMessage: "Set a goal together with your customer!"
      })} {...register("projectGoal")} />
      {submitFailed && (
        <FeedbackMessage variant="ErrorRed"
          message={intl.formatMessage({
            id: "project.editForm.submitFailedMessage",
            defaultMessage: "Project information could not be saved. Please try again."
          })}></FeedbackMessage>
      )}
    </form>
  );
});

export default ProjectForm;
